import { Project } from "projen";

/**
 * Canonical top-level folders for a configulator monorepo (ADR-006).
 *
 * Every sub-project must live under one of these roots. `DOCS` is a
 * singleton (the single Starlight site at `/docs`); `APPS`, `PACKAGES`,
 * and `SITES` are parents for `@scope/<name>` sub-projects.
 *
 * @see docs/src/content/docs/requirements/architectural-decisions/ADR-006-monorepo-layout.md
 */
export const MONOREPO_LAYOUT = {
  DOCS: "docs",
  APPS: "apps",
  PACKAGES: "packages",
  SITES: "sites",
} as const;

export type MonorepoLayoutRoot =
  (typeof MONOREPO_LAYOUT)[keyof typeof MONOREPO_LAYOUT];

/**
 * How `MonorepoProject` reacts when a sub-project's `outdir` falls outside
 * the root expected for its project type (see ADR-006 §4).
 *
 * - `"off"` — no validation runs.
 * - `"warn"` — log a console warning per offending sub-project; synth proceeds.
 * - `"error"` — throw on the first offending sub-project; synth fails.
 */
export const LAYOUT_ENFORCEMENT = {
  OFF: "off",
  WARN: "warn",
  ERROR: "error",
} as const;

export type LayoutEnforcement =
  (typeof LAYOUT_ENFORCEMENT)[keyof typeof LAYOUT_ENFORCEMENT];

/**
 * Mapping from configulator project-type class name to the root folder its
 * `outdir` must live under.
 *
 * We key by class name (not class reference) so this module stays free of
 * import cycles with the project-type files that validate against it.
 * `StarlightProject` maps to `DOCS` by default because the docs-singleton
 * rule says a default-role Starlight project is the monorepo-wide docs
 * site. Site-role Starlight projects override this mapping at validate
 * time via their instance `role` property.
 *
 * @see ADR-006 §4 — Configulator project-type → `outdir` mapping
 */
export const LAYOUT_ROOT_BY_PROJECT_TYPE: Readonly<
  Record<string, MonorepoLayoutRoot>
> = {
  TypeScriptProject: MONOREPO_LAYOUT.PACKAGES,
  AwsCdkProject: MONOREPO_LAYOUT.APPS,
  AstroProject: MONOREPO_LAYOUT.SITES,
  ReactViteSiteProject: MONOREPO_LAYOUT.SITES,
  StarlightProject: MONOREPO_LAYOUT.DOCS,
};

/**
 * A single offender reported by {@link validateMonorepoLayout}. One entry
 * per sub-project whose `outdir` falls outside its expected root.
 */
export interface LayoutViolation {
  readonly projectName: string;
  readonly projectType: string;
  readonly outdir: string;
  readonly expectedRoot: MonorepoLayoutRoot;
}

/**
 * Violation reported by {@link validateStarlightSingleton} when a
 * `MonorepoProject` tree contains more than one `StarlightProject`
 * carrying `role: "docs"` (the docs-singleton rule — ADR-006 §3).
 */
export interface StarlightSingletonViolation {
  readonly projectNames: ReadonlyArray<string>;
}

/**
 * Inspect every sub-project of `root` and collect those whose `outdir` does
 * not live under the root expected for their project class (ADR-006 §4).
 *
 * - Sub-projects whose class name is not in
 *   {@link LAYOUT_ROOT_BY_PROJECT_TYPE} are skipped — the contract only
 *   covers the four documented project types.
 * - The root project itself is skipped (it *is* the monorepo root, not a
 *   sub-project).
 * - `outdir` comparison is done in POSIX form against the project root so
 *   absolute paths synthesised on Windows still match.
 * - A `StarlightProject` carrying `role: "site"` is validated against the
 *   `sites/` root rather than the default `docs/` root. This keeps the
 *   class-to-root mapping static while still honouring the ADR-006 §3
 *   docs-singleton carve-out for docs-role projects.
 */
export function validateMonorepoLayout(root: Project): Array<LayoutViolation> {
  const violations: Array<LayoutViolation> = [];
  const rootOutdir = toPosix(root.outdir);

  for (const sub of root.subprojects) {
    const className = sub.constructor.name;
    const expectedRoot = expectedRootFor(sub, className);

    if (expectedRoot === undefined) {
      // Unknown project type — outside the layout contract.
      continue;
    }

    const relOutdir = relativeOutdir(rootOutdir, toPosix(sub.outdir));

    if (!outdirMatchesRoot(relOutdir, expectedRoot)) {
      violations.push({
        projectName: sub.name,
        projectType: className,
        outdir: relOutdir,
        expectedRoot,
      });
    }
  }

  return violations;
}

/**
 * Inspect every sub-project of `root` and flag cases where more than one
 * `StarlightProject` claims `role: "docs"` (ADR-006 §3 — there is
 * exactly one Starlight docs site per monorepo, and it lives at `/docs`).
 *
 * Returns `undefined` when the singleton rule is satisfied (zero or one
 * docs-role Starlight project). Returns a single violation whose
 * `projectNames` list contains every offending project when two or more
 * docs-role Starlight projects are present — the caller reports the
 * whole set so authors can see every candidate at once rather than
 * fixing them one at a time.
 *
 * Site-role Starlight projects never count toward the singleton limit
 * and are not flagged.
 */
export function validateStarlightSingleton(
  root: Project,
): StarlightSingletonViolation | undefined {
  const docsRoleProjects: Array<string> = [];

  for (const sub of root.subprojects) {
    if (sub.constructor.name !== "StarlightProject") {
      continue;
    }
    if (getStarlightRole(sub) === "docs") {
      docsRoleProjects.push(sub.name);
    }
  }

  if (docsRoleProjects.length <= 1) {
    return undefined;
  }

  return { projectNames: docsRoleProjects };
}

/**
 * Decide which canonical root a given sub-project's `outdir` must live
 * under. For most project types this is a static class-to-root lookup.
 * `StarlightProject` is the one instance-sensitive case: site-role
 * projects live under `sites/` while docs-role projects live at
 * `docs/`.
 */
function expectedRootFor(
  sub: Project,
  className: string,
): MonorepoLayoutRoot | undefined {
  if (className === "StarlightProject") {
    return getStarlightRole(sub) === "site"
      ? MONOREPO_LAYOUT.SITES
      : MONOREPO_LAYOUT.DOCS;
  }
  return LAYOUT_ROOT_BY_PROJECT_TYPE[className];
}

/**
 * Read the `role` property off a `StarlightProject` instance without
 * importing the class (which would create a cycle between this module
 * and `starlight-project.ts`). Returns the raw role string
 * (`"docs"` or `"site"`), falling back to `"docs"` when the property is
 * missing — mirrors the `StarlightProjectOptions.role` default and
 * preserves backward compatibility with older consumers that predate
 * the role field.
 */
function getStarlightRole(sub: Project): "docs" | "site" {
  const role = (sub as unknown as { readonly role?: string }).role;
  return role === "site" ? "site" : "docs";
}

/**
 * Render one {@link LayoutViolation} as an actionable, single-line error
 * message pointing the reader at ADR-006.
 */
export function formatLayoutViolation(violation: LayoutViolation): string {
  const { projectName, projectType, outdir, expectedRoot } = violation;
  const expectedExample =
    expectedRoot === MONOREPO_LAYOUT.DOCS
      ? "docs/"
      : `${expectedRoot}/<scope>/<name>`;
  return (
    `[monorepo-layout] ${projectType} "${projectName}" has outdir "${outdir}", ` +
    `which is outside the expected root "${expectedRoot}/" ` +
    `(expected e.g. "${expectedExample}"). ` +
    "See docs/src/content/docs/requirements/architectural-decisions/ADR-006-monorepo-layout.md."
  );
}

/**
 * Render a {@link StarlightSingletonViolation} as an actionable,
 * single-line error message pointing the reader at ADR-006 §3
 * (docs-singleton carve-out).
 */
export function formatStarlightSingletonViolation(
  violation: StarlightSingletonViolation,
): string {
  const names = violation.projectNames.map((n) => `"${n}"`).join(", ");
  return (
    `[monorepo-layout] Multiple StarlightProject instances with role: "docs" found (${names}). ` +
    `ADR-006 allows exactly one docs-role Starlight project per MonorepoProject. ` +
    `Either remove the duplicates or set role: "site" on the additional ones so they land under sites/. ` +
    "See docs/src/content/docs/requirements/architectural-decisions/ADR-006-monorepo-layout.md."
  );
}

/**
 * Compare a sub-project `outdir` (expressed relative to the monorepo root,
 * POSIX-separated) to its expected top-level root.
 *
 * The `DOCS` root is a singleton: the sub-project must live at exactly
 * `docs/` (or a nested path under it — e.g. `docs/packages/foo`). For the
 * other roots, the outdir must begin with `<root>/` and carry at least one
 * path segment after it.
 */
function outdirMatchesRoot(
  relOutdir: string,
  expectedRoot: MonorepoLayoutRoot,
): boolean {
  const segments = relOutdir.split("/").filter((s) => s.length > 0);
  if (segments.length === 0) {
    return false;
  }

  if (segments[0] !== expectedRoot) {
    return false;
  }

  if (expectedRoot === MONOREPO_LAYOUT.DOCS) {
    // `docs/` singleton — anything at or below docs/ is allowed.
    return true;
  }

  // `apps/`, `packages/`, `sites/` — must have at least one segment after the root.
  return segments.length >= 2;
}

/**
 * Normalise a path to use forward slashes so Windows absolute paths
 * (`C:\...`) compare cleanly against POSIX-style outdir values.
 */
function toPosix(p: string): string {
  return p.replace(/\\/g, "/");
}

/**
 * Convert an absolute sub-project `outdir` into a path relative to the root
 * project. If the two paths are unrelated (which shouldn't happen in
 * practice — projen nests sub-projects under their parent), we fall back to
 * the raw sub-outdir so the violation message still points somewhere
 * meaningful.
 */
function relativeOutdir(rootOutdir: string, subOutdir: string): string {
  if (subOutdir === rootOutdir) {
    return "";
  }
  const prefix = rootOutdir.endsWith("/") ? rootOutdir : `${rootOutdir}/`;
  if (subOutdir.startsWith(prefix)) {
    return subOutdir.slice(prefix.length);
  }
  return subOutdir;
}
